import { useState } from "react";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import i18n from "i18next";
import { SafetyControlSurveyView } from "src/api/generated/erp/db/types/tables/safetyControlSurveyView.ts";
import { CenteredTypography } from "src/components/common/CenteredTypography.tsx";
import { SafetyControlSurveysDataGrid } from "./SafetyControlSurveysDataGrid.tsx";
import { SafetyControlSurveyNotesDataGrid } from "./SafetyControlSurveyNotesDataGrid.tsx";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";
import { useRefreshRef } from "src/utils/useRefreshRef.ts";

export interface SafetyControlSurveysPageProps {
	isActive?: boolean;
	onlySafetyControlSurveyToShowId?: number;
}

export const SafetyControlSurveysPage = ({
	isActive,
	onlySafetyControlSurveyToShowId,
}: SafetyControlSurveysPageProps) => {
	const [selectedSurvey, setSelectedSurvey] = useState<SafetyControlSurveyView | undefined>();
	const [activeSurveysTabKey, setActiveSurveysTabKey] = useState<string | undefined>(
		isActive == null ? undefined
		: isActive ? "active"
		: "closed",
	);
	const surveysRefreshRef = useRefreshRef();
	const notesRefreshRef = useRefreshRef();
	const tasksRefreshRef = useRefreshRef();

	const selectSurveyTypography = <CenteredTypography>{i18n.t("select_survey")}</CenteredTypography>;

	return (
		<AavoMosaic
			viewId={"8654300BDB2F06B9"}
			layout={{
				type: "column",
				first: {
					type: "tabs",
					activeTabItemKey: activeSurveysTabKey,
					onActiveTabItemChanged:
						activeSurveysTabKey == null ? undefined : (key) => setActiveSurveysTabKey(key ?? "active"),
					items: [
						{
							type: "panel",
							key: "active",
							title: i18n.t("active.plural"),
							content: (
								<SafetyControlSurveysDataGrid
									showActiveOnly={true}
									onlySafetyControlSurveyToShowId={onlySafetyControlSurveyToShowId}
									onSelectionChanged={setSelectedSurvey}
									refreshRef={surveysRefreshRef}
								/>
							),
						},
						{
							type: "panel",
							key: "closed",
							title: i18n.t("closed.plural"),
							content: (
								<SafetyControlSurveysDataGrid
									showActiveOnly={false}
									onlySafetyControlSurveyToShowId={onlySafetyControlSurveyToShowId}
									onSelectionChanged={setSelectedSurvey}
									refreshRef={surveysRefreshRef}
								/>
							),
						},
					],
				},
				second: {
					type: "tabs",
					items: [
						{
							type: "panel",
							title: i18n.t("notes"),
							content:
								selectedSurvey == null ? selectSurveyTypography : (
									<SafetyControlSurveyNotesDataGrid
										key={selectedSurvey.safetyControlSurveyId}
										safetyControlSurveyId={selectedSurvey.safetyControlSurveyId}
										surveyState={selectedSurvey.safetyControlSurveyState}
										refreshRef={notesRefreshRef}
									/>
								),
						},
						{
							type: "panel",
							title: i18n.t("tasks"),
							content:
								selectedSurvey == null ? selectSurveyTypography : (
									<SingleSourceTasksView
										key={selectedSurvey.safetyControlSurveyId}
										taskSourceRef={{
											sourceType: "SAFETY_CONTROL_SURVEY",
											sourceId: selectedSurvey.safetyControlSurveyId,
										}}
										refreshRef={tasksRefreshRef}
									/>
								),
						},
					],
				},
			}}
		/>
	);
};
